import { FC, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Button, Form, Input, Row, Typography } from "antd";
import MultipleChoiceView from "./components/MultipleChoiceView";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  amountOptionSelected,
  dynamicAmountValue,
  paymentPageDetail,
  paymentPageInitResponse,
  pagePaymentCompletionStorage,
} from "../../store/store";
import FixedAmountView from "./components/FixedAmountView";
import Util from "../../util/util";
import { initializePaymentPageAPI } from "../../api/paymentPage";
import { IPaymentPageCompletionStorage, IPaymentPageInit } from "@app-models";
import DynamicAmountView from "./components/DynamicAmountView";
import SimpleInputField from "./components/SimpleInputField";
import ValidatedTextFieldInput from "./components/ValidatedTextFieldInput";
import CustomDropdownField from "./components/CustomDropdownField";
import CustomDateField from "./components/CustomDateField";
import GenericAttributeField from "./components/GenericAttributeField";
import MonnifyIcon from "../../common/icons";
import {
  AMOUNT_TYPE,
  CURRENCY_CODE,
  DEFAULT_PAYMENT_METHODS,
  PAYLINK_BASE_URL,
  VALIDATION_TYPE,
} from "./constants";

interface SubmitButtonProps {
  isLoading: boolean;
  amount: number;
}

const SubmitButton: FC<SubmitButtonProps> = ({ isLoading, amount }) => {
  const form = Form.useFormInstance();
  const pageDetail = useRecoilValue(paymentPageDetail);
  const amountSelected = useRecoilValue(amountOptionSelected);
  const dynamicAmount = useRecoilValue(dynamicAmountValue);

  return (
    <Form.Item shouldUpdate noStyle>
      {() => {
        const values = form.getFieldsValue();

        const isFirstNameFilled = !!(values.firstName || "").trim();
        const isLastNameFilled = !!(values.lastName || "").trim();
        const isEmailFilled = !!(values.email || "").trim();
        const isPhoneFilled =
          !pageDetail?.collectPhoneNumber || !!(values.phoneNumber || "").trim();

        let isAmountValid = true;
        if (pageDetail?.amountType === AMOUNT_TYPE.DYNAMIC) {
          isAmountValid = Number(dynamicAmount) > 0;
        } else if (pageDetail?.amountType === AMOUNT_TYPE.FIXED_OPTIONS) {
          isAmountValid = !!amountSelected;
        }

        const customAttrsFilled = (
          Array.isArray(pageDetail?.customAttributes) ? pageDetail!.customAttributes : []
        ).every((attr) => {
          if (!attr.mandatory) return true;
          const val = values[attr.code as string];
          return val !== undefined && val !== null && val !== "";
        });

        const isDisabled =
          !isFirstNameFilled ||
          !isLastNameFilled ||
          !isEmailFilled ||
          !isPhoneFilled ||
          !isAmountValid ||
          !customAttrsFilled;

        return (
          <Form.Item wrapperCol={{ offset: 0 }} style={{ marginTop: 8 }}>
            <Button
              loading={isLoading}
              htmlType="submit"
              type="primary"
              disabled={isDisabled}
              style={{ height: "54px" }}
            >
              Pay {Util.formatAmount(CURRENCY_CODE, amount)}
            </Button>
          </Form.Item>
        );
      }}
    </Form.Item>
  );
};

const PaymentPageLoaded = () => {
  const pageDetail = useRecoilValue(paymentPageDetail);
  const [isLoading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [, setPageInitResponse] = useRecoilState(paymentPageInitResponse);
  const [, setPagePaymentCompletion] = useRecoilState(pagePaymentCompletionStorage);

  const amountSelected = useRecoilValue(amountOptionSelected);
  const dynamicAmount = useRecoilValue(dynamicAmountValue);

  const ogImageUrl = useMemo(() => {
    if (pageDetail?.seoImageUrl) {
      return /^https?:\/\//i.test(pageDetail.seoImageUrl)
        ? pageDetail.seoImageUrl
        : `data:image/png;base64,${pageDetail.seoImageUrl}`;
    }
    if (pageDetail?.merchant?.merchantLogoUrl) {
      return pageDetail.merchant.merchantLogoUrl;
    }
    return `${window.location.origin}/monnify-og-logo.png`;
  }, [pageDetail]);

  const getOptionAmount = () => {
    if (pageDetail?.amountType === AMOUNT_TYPE.DYNAMIC) return Number(dynamicAmount);
    if (pageDetail?.amountType === AMOUNT_TYPE.FIXED) return pageDetail.amountPayable;
    const option = pageDetail?.amountOptions?.find((o) => o.code === amountSelected);
    return option?.payableAmount || 0;
  };

  const renderAmountType = () => {
    switch (pageDetail?.amountType) {
      case AMOUNT_TYPE.FIXED:
        return <FixedAmountView />;
      case AMOUNT_TYPE.FIXED_OPTIONS:
        return <MultipleChoiceView form={form} />;
      case AMOUNT_TYPE.DYNAMIC:
        return <DynamicAmountView form={form} />;
    }
  };

  const onFinishFailed = (_: any) => {};
  const onFinish = async (values: any) => {
    try {
      if (isLoading) return;
      const customValues: Array<{ code: string; value: string }> = [];

      (Array.isArray(pageDetail?.customAttributes) ? pageDetail!.customAttributes : []).forEach(
        (attr) => {
          const code = attr.code as string;
          if (
            typeof values[code] === "object" &&
            values[code]?.$d &&
            values[code]?.$d instanceof Date
          ) {
            customValues.push({ code, value: values[code].format("YYYY-MM-DD") });
          } else {
            customValues.push({ code, value: values[code] });
          }
        }
      );

      const fixedOptionsCode = amountSelected ?? "";

      setLoading(true);

      const payload: IPaymentPageInit = {
        amount: pageDetail?.amountPayable || "",
        fixedOptionsCode,
        contractCode: pageDetail?.contractCode as string,
        currencyCode: CURRENCY_CODE,
        paymentReference: pageDetail?.reference as string,
        customerEmail: values.email,
        customerName: `${values.firstName} ${values.lastName}`,
        customerPhoneNumber: values.phoneNumber && values.phoneNumber,
        paymentMethods: [...DEFAULT_PAYMENT_METHODS],
        redirectUrl: `${window.location.origin}/payment-completion/${pageDetail?.code}`,
        pageAttributes: customValues,
        pageCode: pageDetail?.code as string,
      };

      if (pageDetail?.amountType === AMOUNT_TYPE.FIXED) delete payload.fixedOptionsCode;
      else {
        payload.amount = getOptionAmount();
      }

      const response = await initializePaymentPageAPI(payload);
      setLoading(false);

      if (response) {
        const sessionRetrivalObject: IPaymentPageCompletionStorage = {
          transactionReference: response.transactionReference,
          apiKey: response.apiKey,
          paymentCompletionConfigs: pageDetail?.paymentCompletionConfigs,
          pageData: pageDetail!,
        };

        setPagePaymentCompletion(sessionRetrivalObject);
        setPageInitResponse(response);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  if (pageDetail?.status && pageDetail.status !== "ACTIVE") {
    return (
      <div className="page-inactive-wrapper">
        <div className="page-inactive-icon">
          <MonnifyIcon type="NEW_FAILURE_ICON" />
        </div>
        <Typography.Title level={3} className="page-inactive-title">
          No longer accepting payments
        </Typography.Title>
        <Typography.Text className="page-inactive-support">
          Questions? Reach out to{" "}
          <a href={`mailto:${pageDetail?.merchant?.supportEmail}`}>
            {pageDetail?.merchant?.supportEmail}
          </a>
        </Typography.Text>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <meta property="og:title" content={pageDetail?.name as string} />
        <meta property="og:description" content={pageDetail?.description} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:url" content={`${PAYLINK_BASE_URL}/${pageDetail?.code}`} />
        <meta name="twitter:image" content={ogImageUrl} />
      </Helmet>

      <Typography.Title level={4} className="payment-details-heading">
        Payment Details
      </Typography.Title>

      <Form
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
        form={form}
        data-testid="payment-page-form"
        className="payment-form-card"
      >
        {/* Amount section in tinted card */}
        <div className="amount-section-card">{renderAmountType()}</div>

        {/* Form fields */}
        <div className="form-section-fields">
          <div className="name-container-flex">
            <div className="name-field">
              <Form.Item
                className=""
                name={"firstName"}
                rules={[{ required: true, message: "Please provide your first name" }]}
              >
                <div className="name-field-2">
                  <label className="form-label-style">First name</label>
                  <Input placeholder="First name" className="form-input-style" />
                </div>
              </Form.Item>
            </div>
            <div className="name-field">
              <Form.Item
                name={"lastName"}
                rules={[{ required: true, message: "Please provide your last name" }]}
              >
                <div className="name-field-2">
                  <label className="form-label-style">Last name</label>
                  <Input placeholder="Last name" className="form-input-style" />
                </div>
              </Form.Item>
            </div>
          </div>
          <div className="name-field">
            <Form.Item
              name={"email"}
              rules={[
                {
                  required: true,
                  message: "Please provide your email",
                  validator: Util.validateEmail,
                },
              ]}
            >
              <div className="name-field-2">
                <label className="form-label-style">Email</label>
                <Input placeholder="Email" className="form-input-style" />
              </div>
            </Form.Item>
          </div>

          {pageDetail?.collectPhoneNumber && (
            <div className="name-field">
              <Form.Item
                name={"phoneNumber"}
                rules={[
                  {
                    required: true,
                    message: "Please provide your phone number",
                  },
                  {
                    pattern: /^[0-9]{10}$/,
                    message: "Please enter a valid 10-digit phone number",
                  },
                ]}
              >
                <div className="name-field-2">
                  <label className="form-label-style">Phone Number</label>
                  <Input
                    addonBefore={
                      <span>
                        <span style={{ marginRight: 4 }}>🇳🇬</span>+234
                      </span>
                    }
                    placeholder="Phone Number"
                    data-testid="phoneNumber"
                    className="form-input-style"
                    maxLength={10}
                  />
                </div>
              </Form.Item>
            </div>
          )}

          {(Array.isArray(pageDetail?.customAttributes) ? pageDetail!.customAttributes : []).map(
            (attr, i) => {
              const fieldName = attr.code as string;
              if (attr.validationType === VALIDATION_TYPE.DROPDOWN) {
                return <CustomDropdownField key={i} name={fieldName} attribute={attr} index={i} />;
              }
              if (attr.validationType === VALIDATION_TYPE.DATE) {
                return <CustomDateField key={i} name={fieldName} attribute={attr} index={i} />;
              }
              if (attr.validationType === VALIDATION_TYPE.VALIDATED_TEXT_FIELD) {
                return <ValidatedTextFieldInput key={i} name={fieldName} attribute={attr} />;
              }
              if (
                attr.validationType === VALIDATION_TYPE.TEXT ||
                attr.validationType === VALIDATION_TYPE.NUMBER
              ) {
                return <SimpleInputField key={i} name={fieldName} attribute={attr} />;
              }
              return <GenericAttributeField key={i} name={fieldName} attribute={attr} index={i} />;
            }
          )}

          <SubmitButton isLoading={isLoading} amount={getOptionAmount()} />

          <Row justify={"center"} className="font-size-12 font-weight-400 mt-2">
            <Typography.Text style={{ textAlign: "center", fontSize: 13 }}>
              Questions? Reach out to{" "}
              <a href={`mailto:${pageDetail?.merchant?.supportEmail}`}>
                {pageDetail?.merchant?.supportEmail}
              </a>
            </Typography.Text>
          </Row>
        </div>
      </Form>
    </>
  );
};

export default PaymentPageLoaded;
